import { maxScore } from '@/data/questions'

export interface ReadinessTier {
  level: 'emerging' | 'developing' | 'established' | 'leading'
  title: string
  subtitle: string
  description: string
  recommendations: string[]
  nextSteps: string[]
}

export function getTier(score: number): ReadinessTier {
  const percent = (score / maxScore) * 100

  if (percent < 35) {
    return {
      level: 'emerging',
      title: 'Emerging',
      subtitle: 'Emerging',
      description:
        'You are in the early stages of building the foundations for AI. Start by organizing your data and mapping your business processes to lay the groundwork for future AI adoption.',
      recommendations: [
        'Inventory your data and build a unified data platform',
        'Digitize and standardize core business processes',
        'Run AI literacy training for executive leadership',
        'Plan small-scale PoCs around focused use cases',
      ],
      nextSteps: [
        'Run a current-state assessment and define a roadmap (1–2 months)',
        'Launch a data-platform PoC (3–6 months)',
        'Stand up a cross-functional DX leadership team',
      ],
    }
  }

  if (percent < 60) {
    return {
      level: 'developing',
      title: 'Developing',
      subtitle: 'Developing',
      description:
        'Your foundations are coming together and you are at the entry point of AI adoption. The key is to focus on priority use cases and move from PoC to real production deployments.',
      recommendations: [
        'Strengthen data integration and automate quality management',
        'Move priority use cases from PoC into production',
        'Establish a dedicated AI team and invest in talent development',
        'Build out cloud / MLOps infrastructure step by step',
      ],
      nextSteps: [
        'Productionize 2–3 priority use cases (6–12 months)',
        'Launch AI talent acquisition and upskilling programs',
        'Define organization-wide AI usage guidelines',
      ],
    }
  }

  if (percent < 85) {
    return {
      level: 'established',
      title: 'Established',
      subtitle: 'Established',
      description:
        'AI is taking root in your organization, and you are seeing results across multiple areas. To unlock more value, focus on scaling success across the business and strengthening governance.',
      recommendations: [
        'Scale successful patterns across other teams and domains',
        'Operate MLOps / LLMOps platforms in full production',
        'Strengthen AI governance and ethical review processes',
        'Define a strategy for generative AI and AI agents',
      ],
      nextSteps: [
        'Establish an AI Center of Excellence (CoE)',
        'Design and operate company-wide AI KPIs',
        'Launch co-creation projects with external partners',
      ],
    }
  }

  return {
    level: 'leading',
    title: 'Leading',
    subtitle: 'Leading',
    description:
      'You are at industry-leading levels of AI maturity. Position AI at the center of your competitive advantage and drive innovative business models that set the pace for the industry.',
    recommendations: [
      'Launch AI-native products and new lines of business',
      'Build cross-industry AI ecosystems and partnerships',
      'Establish global-grade AI ethics and governance',
      'Invest in frontier AI research and real-world deployment',
    ],
    nextSteps: [
      'Scale your AI strategy globally',
      'Engage actively in shaping industry standards and regulation',
      'Build a global AI talent acquisition and development strategy',
    ],
  }
}

export function getCategoryScores(answers: Record<string, number>, questions: { id: string; category: string }[]) {
  const map: Record<string, { total: number; count: number }> = {}
  for (const q of questions) {
    const score = answers[q.id] || 0
    if (!map[q.category]) map[q.category] = { total: 0, count: 0 }
    map[q.category].total += score
    map[q.category].count += 1
  }
  return Object.entries(map).map(([category, { total, count }]) => ({
    category,
    score: total,
    max: count * 4,
    percent: Math.round((total / (count * 4)) * 100),
  }))
}
